import { DateTime } from 'luxon';
import { SortOrder } from '../../types';
import { Invoice, SortInvoicesBy } from './invoices.types';

export const InvoicesKey = 'invoices';

export const multiSort = (
  invoices: Invoice[],
  sortConfig: { by: SortInvoicesBy; order: SortOrder }[],
) => {
  // Copy of array so we don't sort in place, maintain immutability
  return [...(invoices ?? [])].sort((a, b) => {
    // Compose multiple comparators by their configured order, with tie-breakers
    const prioritisedConfig = sortConfig.sort((a, b) => {
      const byA = sortableColumnsPriority[a.by];
      const byB = sortableColumnsPriority[b.by];
      return byA - byB;
    });

    for (const { by, order } of prioritisedConfig) {
      const comparator = sortInvoicesMap[by];
      if (!comparator) continue;
      let result = comparator(a, b);
      // If descending, invert result
      if (order === SortOrder.Descending) result *= -1;
      if (result !== 0) return result;
      // If equal, try next sort definition
    }
    // if still equal after all sorts, maintain original order
    return 0;
  });
};

export const sortInvoicesMap: Record<
  SortInvoicesBy,
  (a: Invoice, b: Invoice) => number
> = {
  [SortInvoicesBy.INVOICE_ID]: (a, b) => a.id.localeCompare(b.id),
  [SortInvoicesBy.PROJECT_NAME]: (a, b) =>
    a.projectName?.localeCompare(b.projectName ?? '') ?? 0,
  [SortInvoicesBy.AMOUNT]: (a, b) => a.amount - b.amount,
  [SortInvoicesBy.DEBT]: (a, b) => (a.debt ?? 0) - (b.debt ?? 0),
  [SortInvoicesBy.AMOUNT_PAID]: (a, b) =>
    (a.amountPaid ?? 0) - (b.amountPaid ?? 0),
  [SortInvoicesBy.INVOICE_DUE_DATE]: (a, b) => {
    const aDate = a.invoiceDueDate
      ? DateTime.fromFormat(a.invoiceDueDate as string, 'MM/dd/yyyy')
      : DateTime.now();
    const bDate = b.invoiceDueDate
      ? DateTime.fromFormat(b.invoiceDueDate as string, 'MM/dd/yyyy')
      : DateTime.now();

    return aDate.diff(bDate).toMillis();
  },
  [SortInvoicesBy.PAID_ON]: (a, b) => {
    const aDate = a.paidOn
      ? DateTime.fromFormat(a.paidOn as string, 'MM/dd/yyyy')
      : DateTime.now();
    const bDate = b.paidOn
      ? DateTime.fromFormat(b.paidOn as string, 'MM/dd/yyyy')
      : DateTime.now();

    return aDate.diff(bDate).toMillis();
  },
  [SortInvoicesBy.INVOICE_SENT]: (a, b) => {
    const aDate = a.whencreated
      ? DateTime.fromFormat(a.whencreated as string, 'MM/dd/yyyy')
      : DateTime.now();
    const bDate = b.whencreated
      ? DateTime.fromFormat(b.whencreated as string, 'MM/dd/yyyy')
      : DateTime.now();

    return aDate.diff(bDate).toMillis();
  },
  [SortInvoicesBy.OVERDUE]: (a) => (a.isPaidAfterPastDue ? 1 : -1),
};

export const sortableColumns: Record<string, SortInvoicesBy> = {
  'Invoice id': SortInvoicesBy.INVOICE_ID,
  'Project name': SortInvoicesBy.PROJECT_NAME,
  Amount: SortInvoicesBy.AMOUNT,
  Debt: SortInvoicesBy.DEBT,
  Paid: SortInvoicesBy.AMOUNT_PAID,
  'Due date': SortInvoicesBy.INVOICE_DUE_DATE,
  'Invoice sent': SortInvoicesBy.INVOICE_SENT,
  'Paid on': SortInvoicesBy.PAID_ON,
  Overdue: SortInvoicesBy.OVERDUE,
};

export const sortableColumnsPriority: Record<string, number> = {
  [SortInvoicesBy.INVOICE_DUE_DATE]: 1,
  [SortInvoicesBy.AMOUNT]: 2,
  [SortInvoicesBy.INVOICE_SENT]: 3,
  [SortInvoicesBy.PAID_ON]: 4,
  [SortInvoicesBy.DEBT]: 5,
  [SortInvoicesBy.AMOUNT_PAID]: 6,
  [SortInvoicesBy.OVERDUE]: 7,
  [SortInvoicesBy.PROJECT_NAME]: 8,
  [SortInvoicesBy.INVOICE_ID]: 9,
};
